import * as Sentry from '@sentry/react';

/**
 * Returns True if the SENTRY_RELEASE environment variable is set in window.config.
 * @type {boolean}
 */
const hasVersion = !!window?.config?.SENTRY_RELEASE || false;

/**
 * We only want to initialize Sentry if the SENTRY_DSN environment variable is set in window.config.
 */
if (hasVersion) {
  Sentry.init({
    dsn: 'https://0f18c1196ecd46959727d608d68354c4@o939.ingest.sentry.io/39961',
    release: window?.config?.SENTRY_RELEASE || undefined,
    environment: 'prod',

    integrations: [
      ...Sentry.defaultIntegrations,
      new Sentry.Integrations.GlobalHandlers({
        onerror: false,
        onunhandledrejection: false
      })
    ],

    // debug: true, // This should be disabled in Production.

    initialScope: {
      tags: { logger: 'javascript' }
    },

    // Filtering. See:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/
    ignoreErrors: [
      // Random Plugins/Extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "Optimizing" Proxy
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',

      'conduitPage',

      // Generic Error Code
      'Script error',

      // Webpack Error
      'ChunkLoadError'
    ],
    denyUrls: [
      // Facebook Flakiness
      /graph\.facebook\.com/i,
      // Facebook Blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra Flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome Extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other Plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
    ],
    allowUrls: [
      /[a-zA-Z]+?\.uniid\.it/,
      /[a-zA-Z]+?\.?(unisport|unisportstore)\.[a-zA-Z]+/
    ],

    beforeSend: (event, hint) => {
      if (hint) {
        /**
         * Checks if the Error Stack contains a string matching "undefined is not an object".
         */
        const errorRegex = /^undefined is not an object/;

        if (hint.originalException?.message?.match?.(errorRegex)) {
          return null;
        }

        /**
         * Checks if the Error Stack contains a string that matches Google Tag Manager.
         */
        const isGTM =
          hint.originalException?.stack?.indexOf(
            'www.googletagmanager.com/gtm.js?id='
          ) > -1 || false;
        if (isGTM) {
          return null;
        }

        /**
         * Checks if the Error Stack contains a string that matches Dixa.io.
         */
        const isDixa =
          hint.originalException?.stack?.indexOf('dixa.io') > -1 || false;
        if (isDixa) {
          return null;
        }

        const error = hint.originalException;
        let errorMessage;

        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        switch (errorMessage) {
          case 'Error':
          case 'Network Error':
          case /^Error$/:
          case /^Network Error$/:
            return null;
          default:
            break;
        }
      }
      return event;
    },

    // tracesSampleRate should be 0, as it puts a lot of load on the Sentry servers.
    tracesSampleRate: 0

    // sampleRate can be enabled, if you want to sample Errors.
    // sampleRate: 0.2
  });
}
